import React, { useEffect, useState } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { DenseTable } from '../shared/DenseTable';
import { StatusBadge } from '../shared/StatusBadge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Plus, Send, Edit, RefreshCw, Mail, MessageSquare, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function MarketingPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [form, setForm] = useState({
    name: '',
    campaign_type: 'EMAIL',
    target_audience: 'ALL_CLIENTS',
    content: '',
    scheduled_at: '',
    status: 'DRAFT'
  });

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/marketing/campaigns`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
      }
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const handleSave = async () => {
    try {
      const url = editingCampaign 
        ? `${API_URL}/api/marketing/campaigns/${editingCampaign.campaign_id}`
        : `${API_URL}/api/marketing/campaigns`;
      
      const response = await fetch(url, {
        method: editingCampaign ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form)
      });
      
      if (response.ok) {
        toast.success(editingCampaign ? 'Campaign updated' : 'Campaign created');
        setShowForm(false);
        setEditingCampaign(null);
        setForm({
          name: '',
          campaign_type: 'EMAIL',
          target_audience: 'ALL_CLIENTS',
          content: '',
          scheduled_at: '',
          status: 'DRAFT'
        });
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save campaign');
      }
    } catch (error) {
      toast.error('Failed to save campaign');
    }
  };

  const handleSend = async (campaignId) => {
    try {
      const response = await fetch(`${API_URL}/api/marketing/campaigns/${campaignId}/send`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Campaign sent successfully');
        fetchCampaigns();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to send campaign');
      }
    } catch (error) {
      toast.error('Failed to send campaign');
    }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setForm({
      name: campaign.name,
      campaign_type: campaign.campaign_type,
      target_audience: campaign.target_audience,
      content: campaign.content,
      scheduled_at: campaign.scheduled_at || '',
      status: campaign.status
    });
    setShowForm(true);
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'EMAIL': return <Mail className="h-4 w-4" />;
      case 'SMS': return <Phone className="h-4 w-4" />;
      case 'WHATSAPP': return <MessageSquare className="h-4 w-4" />;
      default: return <Mail className="h-4 w-4" />;
    }
  };

  const columns = [
    { header: 'Name', accessor: 'name' },
    { 
      header: 'Type', 
      render: (row) => (
        <div className="flex items-center gap-1">
          {getTypeIcon(row.campaign_type)}
          <span>{row.campaign_type}</span>
        </div>
      )
    },
    { header: 'Audience', accessor: 'target_audience' },
    { header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    { 
      header: 'Created', 
      render: (row) => format(new Date(row.created_at), 'dd/MM/yy HH:mm')
    },
    { 
      header: 'Actions', 
      render: (row) => (
        <div className="flex gap-1">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => handleEdit(row)}
            data-testid={`edit-campaign-${row.campaign_id}`}
          >
            <Edit className="h-3.5 w-3.5" />
          </Button>
          {row.status === 'DRAFT' && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleSend(row.campaign_id)}
              data-testid={`send-campaign-${row.campaign_id}`}
            >
              <Send className="h-3.5 w-3.5 mr-1" />
              Send
            </Button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="space-y-4" data-testid="marketing-page">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-xl font-bold">Marketing Planner</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchCampaigns}>
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button size="sm" onClick={() => setShowForm(true)} data-testid="create-campaign-btn">
            <Plus className="h-4 w-4 mr-1" />
            New Campaign
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-3">
        <GroupBox label="Total Campaigns" className="p-3">
          <p className="text-2xl font-bold">{campaigns.length}</p>
        </GroupBox>
        <GroupBox label="Draft" className="p-3">
          <p className="text-2xl font-bold text-amber-500">
            {campaigns.filter(c => c.status === 'DRAFT').length}
          </p>
        </GroupBox>
        <GroupBox label="Sent" className="p-3">
          <p className="text-2xl font-bold text-emerald-500">
            {campaigns.filter(c => c.status === 'SENT').length}
          </p>
        </GroupBox>
        <GroupBox label="Scheduled" className="p-3">
          <p className="text-2xl font-bold text-blue-500">
            {campaigns.filter(c => c.status === 'SCHEDULED').length}
          </p>
        </GroupBox>
      </div>

      {/* Campaigns Table */}
      <GroupBox label="Campaigns">
        <DenseTable 
          columns={columns} 
          data={campaigns}
          emptyMessage="No campaigns found. Create your first campaign!"
        />
      </GroupBox>

      {/* Campaign Form Dialog */}
      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); setEditingCampaign(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingCampaign ? 'Edit Campaign' : 'Create Campaign'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Campaign Name</Label>
              <Input 
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="e.g., New Year Promo"
                data-testid="campaign-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={form.campaign_type} onValueChange={(v) => setForm({...form, campaign_type: v})}>
                  <SelectTrigger data-testid="campaign-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EMAIL">Email</SelectItem>
                    <SelectItem value="SMS">SMS</SelectItem>
                    <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                    <SelectItem value="PUSH">Push Notification</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Target Audience</Label>
                <Select value={form.target_audience} onValueChange={(v) => setForm({...form, target_audience: v})}>
                  <SelectTrigger data-testid="campaign-audience-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL_CLIENTS">All Clients</SelectItem>
                    <SelectItem value="ACTIVE_CLIENTS">Active Clients</SelectItem>
                    <SelectItem value="NEW_CLIENTS">New Clients</SelectItem>
                    <SelectItem value="HIGH_VALUE">High Value Clients</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Schedule (Optional)</Label>
              <Input 
                type="datetime-local"
                value={form.scheduled_at}
                onChange={(e) => setForm({...form, scheduled_at: e.target.value})}
                data-testid="campaign-schedule-input"
              />
            </div>
            
            <div>
              <Label>Content</Label>
              <Textarea 
                value={form.content}
                onChange={(e) => setForm({...form, content: e.target.value})}
                placeholder="Enter your campaign message..."
                rows={4}
                data-testid="campaign-content-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowForm(false); setEditingCampaign(null); }}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="save-campaign-btn">
              {editingCampaign ? 'Update' : 'Create'} Campaign
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
